import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import DropdownMenu from '../ui/DropdownMenu';
import { Card } from '../ui/Card';
import DetailPageHeader from '../ui/DetailPageHeader';
import { LockedAtAcceptancePill } from '../quotes/LockedAtAcceptancePill';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { EmptyState } from '../ui/EmptyState';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
import { useToast } from '../ui';
import { useConfirm } from '../ui/ConfirmationModal';
import { useSettingsContext } from '../../hooks/useSettings';
import { MASTER_TAX_DISCLAIMER } from '../../constants/disclaimers';
import api, { jobAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/formatDate';
import { invoiceStatus, isInvoiceSent, canMarkInvoicePaid, lineQuantity, lineTotalCents, deriveLineRate, jobHeaderLabel, invoiceDeliveries } from './invoiceLifecycle';
import DeliveryList from './DeliveryList';
import SendAgainComposer from './SendAgainComposer';
import useTelemetry from '../../hooks/useTelemetry';
import MarkPaidConfirm from './MarkPaidConfirm';
import { useOptionalAuth } from '../../contexts/AuthContext';

// IDI-817 D2: before Send, a split completion draft's page shows what Send
// will bill (the saved-invoice preview: recomputed to what's left, with the
// job's current lines when a change order moved the total). Nothing is
// written; the Invoices list keeps the saved figure. Guarded: some callers'
// api lacks previewInvoice, and a failed preview leaves the saved record.
async function withSendFigure(inv) {
  if (!inv) return inv;
  const isDraft = String(inv.status || '').toLowerCase() === 'draft';
  const isCompletion = String(inv.splitType || '').toLowerCase() === 'completion';
  if (!isDraft || !isCompletion || typeof api.previewInvoice !== 'function') return inv;
  try {
    const res = await api.previewInvoice({ invoice_id: inv.id });
    const p = res?.data || res;
    if (!p || p.totalAmount == null) return inv;
    const out = { ...inv };
    for (const k of ['subtotal', 'taxAmount', 'totalAmount', 'lineItems', 'splitJobSubtotal', 'splitRebasedAtSend', 'shareLabel']) {
      if (p[k] !== undefined) out[k] = p[k];
    }
    return out;
  } catch {
    return inv;
  }
}
export default function InvoiceDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  // IDI-538: the job, for the header's "J-000030 · title".
  const [job, setJob] = useState(null);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useOptionalAuth() || {};
  // IDI-481 (Q-12/B-03): only admins and salespeople may delete; the API
  // enforces it too. Drafts only (Q-11).
  const canDelete = user?.role !== 'field_tech';
  const { getEnumLabel } = useSettingsContext();
  const { emit } = useTelemetry();

  // Computed header values
  const backHref = (invoice?.job_id || invoice?.jobId)
    ? `/jobs/${invoice.job_id || invoice.jobId}`
    : (invoice?.customer_id || invoice?.customerId)
      ? `/customers/${invoice.customer_id || invoice.customerId}`
      : '/dashboard';
  const backLabel = (invoice?.job_id || invoice?.jobId)
    ? 'Back to Job'
    : (invoice?.customer_id || invoice?.customerId)
      ? 'Back to Customer'
      : 'Back to Dashboard';
  const invoiceTitle = invoice?.invoiceNumber || invoice?.invoice_number || 'Invoice';
  const customerName = invoice?.customerName || invoice?.customer_name || '';
  const invoiceJobId = invoice?.jobId || invoice?.job_id || null;
  // IDI-538: J-number · job title, never "Job #<uuid>"; no job → customer name.
  const jobLabel = invoiceJobId ? jobHeaderLabel(job, invoice?.jobNumber || invoice?.job_number) : '';
  const headerSubtitle = jobLabel || customerName;
  // IDI-337: one normalised stored status + one sent-ness predicate, shared
  // with the job's Invoices list (deriveAging).
  const status = invoiceStatus(invoice);
  // IDI-518: State-B (Send Again, Download PDF, ⋮) covers sent and paid. A
  // stray stored past_due is a Sent invoice's aging, not a state of its own,
  // so it lands here too (and the backend's mark_paid accepts it as sent).
  const sentOrPaid = isInvoiceSent(invoice) || canMarkInvoicePaid(invoice);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchInvoice(); }, [id]);

  useEffect(() => {
    if (!invoiceJobId || !jobAPI?.getJob) return undefined;
    let cancelled = false;
    Promise.resolve(jobAPI.getJob(invoiceJobId))
      .then((res) => { if (!cancelled) setJob(res?.data || res || null); })
      .catch(() => { if (!cancelled) setJob(null); });
    return () => { cancelled = true; };
  }, [invoiceJobId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await api.getInvoice(id);
      setInvoice(await withSendFigure(data));
    }
    catch { setError('Failed to load invoice.'); }
    finally { setLoading(false); }
  };

  // IDI-481 (Q-11): delete a DRAFT. A split's completion draft may go; its
  // deposit stands (the server unlinks it).
  const handleDeleteDraft = async () => {
    const isCompletion = String(invoice?.splitType || '').toLowerCase() === 'completion';
    const ok = await confirm({
      title: 'Delete this draft invoice?',
      message: isCompletion
        ? 'The deposit invoice stays as it is. You can create the completion invoice again later.'
        : 'This draft has not been sent. Deleting it frees its amount to bill again.',
      confirmLabel: 'Delete draft',
      cancelLabel: 'Keep it',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setActionLoading(true);
      await api.deleteInvoice(invoice.id);
      toast.success('Draft invoice deleted');
      const jid = invoice.jobId || invoice.job_id;
      navigate(jid ? `/jobs/${jid}?tab=invoices` : '/invoices');
    } catch (err) {
      toast.error(err?.response?.status === 409 || err?.response?.status === 403
        ? (err.friendlyMessage || 'This invoice cannot be deleted.')
        : 'Failed to delete the draft invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // IDI-518: Mark Paid confirms with a "Paid on" date (MarkPaidConfirm).
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const handleMarkPaid = async (paidDate) => {
    try {
      setActionLoading(true);
      await api.markInvoicePaid(id, paidDate);
      setMarkPaidOpen(false);
      await fetchInvoice();
    }
    catch { setError('Failed to mark as paid.'); setMarkPaidOpen(false); }
    finally { setActionLoading(false); }
  };

  // IDI-518: Mark unpaid (paid -> sent) after a confirm; Mark Paid returns.
  const handleMarkUnpaid = async () => {
    const ok = await confirm({
      title: 'Mark unpaid?',
      message: 'This returns the invoice to Sent and clears its paid date.',
      confirmLabel: 'Mark unpaid',
      tone: 'primary',
    });
    if (!ok) return;
    try { setActionLoading(true); await api.markInvoiceUnpaid(id); await fetchInvoice(); }
    catch { setError('Failed to mark as unpaid.'); }
    finally { setActionLoading(false); }
  };

  // IDI-580: Send Again opens IDI-485's composer pre-filled from the last
  // send (SendAgainComposer); the composer IS the confirm. The status never
  // changes; the invoice reloads for the new delivery rows.
  const [sendAgainOpen, setSendAgainOpen] = useState(false);
  const handleSendAgain = () => setSendAgainOpen(true);

  const handleDownloadPDF = async () => {
    try {
      setActionLoading(true);
      const res = await api.generateInvoicePDF(id);
      const data = res?.data || res;
      // Backend returns base64-encoded PDF
      const pdfBase64 = data?.pdf || data;
      if (!pdfBase64 || typeof pdfBase64 !== 'string') {
        setError('No PDF data received.');
        return;
      }
      // Decode base64 to binary
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${invoice.invoiceNumber || invoice.invoice_number || 'invoice'}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      // IDI-519 (L-10): the first download of a saved, unsent invoice dates
      // it. Say so, and show the new Invoiced date.
      if (data?.dated && data?.invoicedDate) {
        toast.success(`Downloaded. Invoice dated ${formatDate(data.invoicedDate)}.`);
        // The server has already saved the date; update in place (no refetch,
        // which could briefly flash the old "—").
        setInvoice((prev) => (prev ? { ...prev, invoicedDate: data.invoicedDate } : prev));
      }
    } catch (err) { setError('Failed to generate PDF: ' + (err.message || '')); }
    finally { setActionLoading(false); }
  };

  // IDI-278 (2026-09-09): QuickBooks CSV export. Wires the previously-
  // disabled "Download CSV" kebab item to api.exportInvoiceQBO (services/
  // api.js:425) and fires the pre-existing telemetry event
  // 'invoice_exported_qbo' (useTelemetry.js:39). Response shape from
  // cmd/invoices/main.go exportQBO: { csv: <base64>, content_type,
  // filename }. Validation errors return 400 with { success: false,
  // validation_errors: […] } and are surfaced through the same <Alert>
  // sink the PDF handler uses — users need to see WHICH validation
  // failed, not a generic 'Failed'.
  const handleDownloadCSV = async () => {
    try {
      setActionLoading(true);
      const data = await api.exportInvoiceQBO(id);
      const csvBase64 = data?.csv;
      const filename = data?.filename || `${invoice.invoiceNumber || invoice.invoice_number || 'invoice'}.csv`;
      if (!csvBase64 || typeof csvBase64 !== 'string') {
        setError('No CSV data received.');
        return;
      }
      // Decode base64 to a blob and click a synthesized download link,
      // mirroring handleDownloadPDF's approach for consistency.
      const binaryString = atob(csvBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: data?.content_type || 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('QuickBooks CSV downloaded');
      emit('invoice_exported_qbo', { invoice_id: id });
    } catch (err) {
      // IDI-278 follow-up (2026-09-09, second pass): `exportQBO` has SIX
      // error paths and ONLY ONE carries `validation_errors[]`. The other
      // five return a plain envelope `{success:false, message:"..."}` from
      // `pkg/response.Error(...)` — or, if API Gateway's authorizer rejects
      // the request before it reaches the Lambda, `{message:"Unauthorized"}`
      // with no `success` key at all. All three carry the useful text under
      // the `message` key (lowercase, per the Go struct's `json:"message"`
      // tag — the field name Message is Go-exported but the wire format is
      // lowercase).
      //
      // 1) validation_errors[] present  → map to `${field}: ${message}` and
      //    join, so the user knows what to fix on the invoice.
      // 2) message present               → surface it verbatim (e.g. "Invoice
      //    not found", "invoice_id is required", "Unauthorized").
      // 3) neither present               → fall back to err.message from axios.
      //
      // Reasoning trace: original PR #387 assumed validation_errors was
      // `string[]`; live-verify on Sample Dealer's I-000015 caught the
      // `[object Object]` render. Operator ruling then flagged the missing
      // envelope shapes.
      const payload = err?.response?.data;
      if (payload?.validation_errors && Array.isArray(payload.validation_errors) && payload.validation_errors.length > 0) {
        const formatted = payload.validation_errors.map((e) => {
          if (e && typeof e === 'object' && (e.field || e.message)) {
            return e.field ? `${e.field}: ${e.message || ''}` : (e.message || String(e));
          }
          return String(e);
        });
        setError('Export blocked by validation: ' + formatted.join('; '));
      } else if (payload && typeof payload.message === 'string' && payload.message) {
        setError('Failed to export CSV: ' + payload.message);
      } else {
        setError('Failed to export CSV: ' + (err?.message || 'Unknown error'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="space-y-4 px-6"><SkeletonCard /><SkeletonCard /></div>;
  if (!invoice) return <Alert variant="error">Invoice not found.</Alert>;

  const lineItems = invoice.line_items || invoice.lineItems || [];
  const subtotal = invoice.subtotal ?? lineItems.reduce((s, i) => s + ((i.line_total ?? i.lineTotal ?? i.amount) || 0), 0);
  // DEF-087 (W1.2b/c): gate the line-items table + surface an inconsistency
  // notice when the invoice asserts a balance with no line items backing it.
  const hasLines = Array.isArray(lineItems) && lineItems.length > 0;
  const hasStaleBalance = !hasLines && Number(subtotal) > 0;
  const taxAmount = invoice.taxAmount ?? invoice.tax_amount ?? 0;
  const taxRatePercent = invoice.taxRatePercent || invoice.tax_rate_percent || 0;
  // IDI-512: a signed quote's discount, carried onto the invoice.
  const discountAmount = Number(invoice.discountAmount ?? invoice.discount_amount ?? 0) || 0;
  const total = invoice.totalAmount ?? invoice.total_amount ?? subtotal - discountAmount + taxAmount;
  const taxSnapshotDate = invoice.taxSnapshotDate || invoice.tax_snapshot_date || '';
  // IDI-817 D5: the PDF's split rows, only when the server gave its label and
  // the job total (as the PDF needs SplitJobSubtotal > 0).
  const splitJobSubtotal = Number(invoice.splitJobSubtotal ?? 0) || 0;
  const isSplitHalf = !!invoice.shareLabel && splitJobSubtotal > 0;


  return (
    <div className="px-6">
      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <DetailPageHeader
        backHref={backHref}
        backLabel={backLabel}
        title={invoiceTitle}
        subtitle={headerSubtitle}
        status={status}
        pills={
          (invoice.acceptedConfigurationId || invoice.accepted_configuration_id)
            ? (
              <LockedAtAcceptancePill
                lockedTotalCents={invoice.lockedTotalCents ?? invoice.locked_total_cents}
                acceptedConfigurationId={invoice.acceptedConfigurationId || invoice.accepted_configuration_id}
                quoteId={invoice.quoteId || invoice.quote_id}
              />
            )
            : undefined
        }
        actions={
          <>
            {/* State-A: draft */}
            {!sentOrPaid && status === 'draft' && (
              <>
                {/* IDI-538: Review & Send confirms (and is where IDI-485's
                    composer goes); no one-click send from the draft page. */}
                <Button onClick={() => navigate(`/invoices/${id}/review`)} disabled={actionLoading}>Review &amp; Send</Button>
                <Button variant="secondary" onClick={handleDownloadPDF} loading={actionLoading}>Download PDF</Button>
                {canDelete && (
                  <DropdownMenu
                    ariaLabel="More actions"
                    items={[{ label: 'Delete draft', onClick: handleDeleteDraft, disabled: actionLoading }]}
                  />
                )}
              </>
            )}
            {/* State-B: sent or paid */}
            {sentOrPaid && (
              <>
                <Button onClick={handleSendAgain} loading={actionLoading}>Send Again</Button>
                <Button variant="secondary" onClick={handleDownloadPDF} loading={actionLoading}>Download PDF</Button>
                {canMarkInvoicePaid(invoice) && (
                  <Button variant="secondary" onClick={() => setMarkPaidOpen(true)} loading={actionLoading}>
                    <CheckIcon className="h-4 w-4" />
                    Mark Paid
                  </Button>
                )}
                {/* IDI-518: Sent ⋮ = Download CSV; Paid adds Mark unpaid.
                    No Mark Unsent, Void or Cancel on any invoice. */}
                <DropdownMenu
                  ariaLabel="More actions"
                  items={[
                    { label: 'Download CSV', onClick: handleDownloadCSV, disabled: actionLoading },
                    ...(status === 'paid'
                      ? [{ label: 'Mark unpaid', onClick: handleMarkUnpaid, disabled: actionLoading }]
                      : []),
                  ]}
                />
              </>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Items */}
        <div className="lg:col-span-2">
          <Card>
            {/* DEF-087 (W1.2b): render EmptyState instead of a bare <thead>
                when the invoice has no line items. Copy per REQ-SYS-EMPTY-01. */}
            {hasLines ? (
              <Table bordered={false}>
                <TableHead>
                  <TableRow>
                    <TableHeader>Item</TableHeader>
                    <TableHeader align="right">Qty</TableHeader>
                    <TableHeader align="right">Rate</TableHeader>
                    <TableHeader align="right">Total</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, idx) => {
                    // IDI-337 (ruled 09-30): RATE = line total ÷ stored qty, to
                    // the cent; blank when qty is 0/missing.
                    const qty = lineQuantity(item);
                    const rate = deriveLineRate(item);
                    return (
                    <TableRow key={item.id || idx}>
                      <TableCell>
                        <p className="font-medium">{item.item_name || item.description || item.name}</p>
                        {item.description && item.item_name && <p className="text-xs text-gray-500">{item.description}</p>}
                      </TableCell>
                      <TableCell align="right" data-testid={`line-qty-${idx}`}>{qty === null ? '' : qty.toLocaleString('en-US')}</TableCell>
                      <TableCell align="right" data-testid={`line-rate-${idx}`}>{rate === null ? '' : formatCurrency(rate)}</TableCell>
                      <TableCell align="right" className="font-semibold" data-testid={`line-total-${idx}`}>{formatCurrency(lineTotalCents(item))}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div data-testid="invoice-detail-line-items-empty">
                <EmptyState mode="first-run" title="No line items on this invoice." />
              </div>
            )}
            {/* DEF-087 (W1.2c): inconsistency notice when the invoice has a
                positive balance but no line items. Invoice-specific copy per
                response §6 ("balance" not "subtotal"). Alert receives NO
                icon child — Alert owns its variant icon (DEF-090). */}
            {hasStaleBalance && (
              <div className="p-4" data-testid="invoice-detail-inconsistency-notice">
                <Alert variant="warning">
                  This invoice has a balance but no line items. The record may be incomplete — contact support before sending.
                </Alert>
              </div>
            )}
            <div className="border-t border-[color:var(--color-border)] px-4 py-3 space-y-1">
              {isSplitHalf ? (
                <>
                  {/* IDI-817 D5: a split half lists the job's lines in full and
                      bills a share, as the PDF does: Job total, Job discount,
                      then the server's share label (never built here). */}
                  <div className="flex justify-between text-sm" data-testid="invoice-detail-job-total"><span>Job total</span><span>{formatCurrency(splitJobSubtotal)}</span></div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm"><span>Job discount</span><span>-{formatCurrency(discountAmount)}</span></div>
                  )}
                  <div className="flex justify-between text-sm" data-testid="invoice-detail-share"><span>{invoice.shareLabel}</span><span>{formatCurrency(subtotal)}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm" data-testid="invoice-detail-subtotal"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                  {/* IDI-512: the signed discount, so Subtotal - Discount + Tax = Total. */}
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="invoice-detail-discount"><span>Discount</span><span>-{formatCurrency(discountAmount)}</span></div>
                  )}
                </>
              )}
              <div className="flex justify-between text-sm text-gray-500"><span>Tax</span><span>{formatCurrency(taxAmount)}{(() => { const parts = []; if (taxRatePercent) parts.push(`${taxRatePercent.toFixed(2)}%`); if (taxSnapshotDate) parts.push(`as of ${formatDate(taxSnapshotDate)}`); return parts.length > 0 ? ` (${parts.join(' — ')})` : ''; })()}</span></div>
              <div className="flex justify-between text-base font-bold pt-1 border-t border-gray-100" data-testid="invoice-detail-total"><span>Total</span><span>{formatCurrency(total)}</span></div>
            </div>
            <p className="px-4 pb-3 text-xs text-gray-400 italic">{MASTER_TAX_DISCLAIMER}</p>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Invoice Details</h3>
            {/* IDI-519: the send (or first download) date; "—" until then. */}
            <div><p className="text-xs text-gray-500">Invoiced</p><p className="text-sm" data-testid="invoice-detail-invoiced-date">{formatDate(invoice.invoiced_date || invoice.invoicedDate) || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Due Date</p><p className="text-sm" data-testid="invoice-detail-due-date">{formatDate(invoice.due_date || invoice.dueDate) || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Terms</p><p className="text-sm">{getEnumLabel('payment_terms', invoice.terms) || invoice.terms || 'Net 30'}</p></div>
            {status === 'paid' && <div><p className="text-xs text-gray-500">Paid Date</p><p className="text-sm text-green-600 font-medium">{formatDate(invoice.paid_date || invoice.paidDate) || '—'}</p></div>}
            <div><p className="text-xs text-gray-500">Created</p><p className="text-sm">{formatDate(invoice.created_at || invoice.createdAt) || '—'}</p></div>
            {(invoice.job_id || invoice.jobId) && (
              <div><p className="text-xs text-gray-500">From Job</p><Link to={`/jobs/${invoice.job_id || invoice.jobId}`} className="text-sm text-primary-600 hover:underline">{invoice.jobNumber || invoice.job_number || 'View Job'}</Link></div>
            )}
            {/* IDI-132: /quotes/:id resolves the landing — a sent or signed
                quote redirects to the locked builder, a converted one shows
                View Job (QuoteDetail lockedLandingRedirect). The invoice row
                carries no quote status for getQuoteRoute to read. */}
            {(invoice.quote_id || invoice.quoteId) && (
              <div><p className="text-xs text-gray-500">From Quote</p><Link to={`/quotes/${invoice.quote_id || invoice.quoteId}`} className="text-sm text-primary-600 hover:underline">{invoice.quoteNumber || invoice.quote_number || 'View Quote'}</Link></div>
            )}
          </Card>
          {(invoice.notes || invoice.memo) && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</h3>
              <p className="text-sm text-gray-700">{invoice.notes || invoice.memo}</p>
            </Card>
          )}
          {/* IDI-485: each recipient of the last send: sent / redirected / failed. */}
          {invoiceDeliveries(invoice).length > 0 && (
            <Card className="p-4">
              <DeliveryList invoice={invoice} />
            </Card>
          )}
        </div>
      </div>

      {markPaidOpen && (
        <MarkPaidConfirm
          open={markPaidOpen}
          invoiceNumber={invoice.invoiceNumber || invoice.invoice_number}
          busy={actionLoading}
          onConfirm={handleMarkPaid}
          onClose={() => setMarkPaidOpen(false)}
        />
      )}
      {sendAgainOpen && invoice && (
        <SendAgainComposer
          invoice={invoice}
          onClose={() => setSendAgainOpen(false)}
          onSent={fetchInvoice}
        />
      )}
    </div>
  );
}
